import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { AlertTriangle, ExternalLink, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const DISMISS_KEY = 'stripe-setup-banner-dismissed-until';

export default function StripeSetupBanner() {
  const [status, setStatus] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    // Hide for 6 hours if user clicked "Remind me later"
    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedUntil > Date.now()) {
      setHidden(true);
      return;
    }
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/vendors/me`).catch(() => null),
      axios.get(`${API}/vendors/stripe/status`).catch(() => null),
    ]).then(([vendorRes, statusRes]) => {
      if (cancelled) return;
      setVendor(vendorRes?.data || null);
      setStatus(statusRes?.data || { connected: false, payouts_enabled: false });
    });
    return () => { cancelled = true; };
  }, []);

  if (hidden) return null;
  if (!vendor || !status) return null;
  // Only show for approved vendors who haven't enabled payouts
  if (vendor.status !== 'approved') return null;
  if (status.payouts_enabled) return null;

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await axios.post(`${API}/vendors/stripe/connect`);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start Stripe onboarding. Please try again.');
      setConnecting(false);
    }
  };

  const handleDismiss = () => {
    // Snooze for 6 hours
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 6 * 60 * 60 * 1000));
    setHidden(true);
  };

  // Different message depending on whether they've started Stripe at all
  const hasStarted = status.connected && !status.payouts_enabled;
  const title = hasStarted
    ? 'Finish your Stripe setup to start receiving payouts'
    : "Connect Stripe — you can't receive payments yet";
  const subtitle = hasStarted
    ? 'You started onboarding but Stripe still needs a few details (bank account, ID, etc.) before payouts unlock.'
    : 'Even if a buyer purchases from your store today, the money cannot reach your bank account until Stripe is connected. This takes 5 minutes.';

  return (
    <div
      data-testid="stripe-setup-banner"
      className="relative bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500 border-y border-r border-orange-200 rounded-lg p-4 mb-6 shadow-sm"
      role="alert"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded hover:bg-orange-100 text-orange-600"
        title="Remind me later (6 hours)"
        data-testid="dismiss-stripe-banner"
        aria-label="Dismiss for 6 hours"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <div className="shrink-0 w-10 h-10 bg-orange-500/15 rounded-full flex items-center justify-center">
          <AlertTriangle className="h-5 w-5 text-orange-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-orange-900 text-sm md:text-base">
            {title}
          </h3>
          <p className="text-orange-800/90 text-xs md:text-sm mt-1 leading-relaxed">
            {subtitle}
          </p>
          <ul className="text-xs text-orange-800/80 mt-2 space-y-0.5">
            <li>· Takes about 5 minutes</li>
            <li>· Bank account, government ID, business details</li>
            <li>· Stripe handles 1099 tax forms automatically</li>
            <li>
              · Founding Vendor rate <strong>{Math.round((vendor.commission_rate || 0.07) * 100)}%</strong>{' '}
              kicks in the moment you&apos;re connected
            </li>
          </ul>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
              data-testid="banner-connect-stripe"
              size="sm"
            >
              {connecting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Opening Stripe…
                </>
              ) : (
                <>
                  <ExternalLink className="h-4 w-4" />
                  {hasStarted ? 'Finish Stripe Setup' : 'Connect Stripe Now'}
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={handleDismiss}
              size="sm"
              className="text-orange-800 hover:text-orange-900 hover:bg-orange-100"
              data-testid="snooze-stripe-banner"
            >
              Remind me later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
